/**
 * Frontend alarm types — canonical mirror of backend/src/types/alarm.types.ts.
 *
 * Contract invariants (Phase 1-4 overhaul, 2026-04-18):
 *   - Severity is 3-tier ONLY: critical / warning / info. Wire `urgent` maps to
 *     `critical` at ingest (normalizer); the UI never sees or renders `urgent`.
 *   - Transition is a closed set of 7 values; all 7 must render distinctly in
 *     the timeline (raised, cleared, underlying_clear, acknowledged,
 *     state_change, communication_loss, communication_restored).
 *   - Identity is `(site_id, device_id, condition_key)` at every list-mutation
 *     site — NEVER collapse by device_id alone.
 *   - Dual-state rows (both cleared_at and acked_at set) are legitimate
 *     transients awaiting the 60s cleanup loop; don't filter them out.
 *   - Pane-of-glass: no ack/clear/resolve writes from UI. Status is derived
 *     from edge-reported column state via deriveAlarmStatus().
 */

export type Severity = 'critical' | 'warning' | 'info';

export type Transition =
  | 'raised'
  | 'cleared'
  | 'underlying_clear'
  | 'acknowledged'
  | 'state_change'
  | 'communication_loss'
  | 'communication_restored';

export type Category = 'fault' | 'warning' | 'state_change' | 'digital_io';

export type AlarmStatus = 'active' | 'awaiting_ack' | 'acknowledged' | 'stale';

/**
 * Active alarm row as returned by GET /api/v1/sites/:siteId/alarms.
 * Mirrors backend AlarmQueryService.listActiveForSite result rows.
 */
export interface ActiveAlarm {
  site_id: string;
  device_id: string;
  condition_key: string;
  customer_id: string;
  device_type: string;
  block_id: string | null;
  condition_label: string;
  category: Category;
  severity: Severity;
  raised_at: string;
  last_event_id: string;
  last_ts: string;
  value: string | null;
  metadata: Record<string, unknown>;
  acked_at: string | null;
  acked_by: string | null;
  underlying_cleared_at: string | null;
  cleared_at: string | null;
  stale_since: string | null;
  latching: boolean;
}

/**
 * Alarm history event as returned by GET /api/v1/sites/:siteId/alarms/history
 * and as the transitions[] entries under the identity-detail endpoint.
 * customer_id is optional on the FE type — the API includes it, but the UI
 * never needs to display it (rows are already site-scoped).
 */
export interface AlarmHistoryEvent {
  event_id: string;
  site_id: string;
  customer_id?: string;
  device_id: string;
  condition_key: string;
  device_type: string;
  block_id: string | null;
  transition: Transition;
  severity: Severity;
  category: Category;
  ts: string;
  condition_label: string;
  value: string | null;
  metadata: Record<string, unknown>;
  lifecycle: Record<string, unknown> | null;
}

/**
 * Site-level summary as returned by GET /api/v1/sites/:siteId/alarms/summary.
 * by_device includes severity counts per device_id for tree badge rendering.
 * Info-tier counts surface only at the per-device level (not site rollup) —
 * matches backend SiteSummaryResult.
 */
export interface AlarmSummary {
  critical: number;
  warning: number;
  awaiting_ack: number;
  stale: number;
  needs_attention: number;
  acknowledged: number;
  by_device: Record<
    string,
    { critical?: number; warning?: number; info?: number; awaiting_ack?: number; stale?: number }
  >;
}

export interface AlarmsListResponse {
  alarms: ActiveAlarm[];
  total: number;
  summary: {
    critical: number;
    warning: number;
    awaiting_ack: number;
    stale: number;
  };
}

export interface AlarmDetailResponse {
  active: ActiveAlarm | null;
  transitions: AlarmHistoryEvent[];
}

/**
 * Device roster entry for the tree. Source: GET /api/v1/sites/:siteId/devices,
 * backed by config.site_devices (soft-delete filter via removed_at IS NULL).
 */
export interface SiteDevice {
  device_id: string;
  device_type: string;
  block_id: string | null;
  display_name: string;
  status: string;
}

/**
 * Derive UI status from an ActiveAlarm row. Order matters:
 *   stale → acknowledged → awaiting_ack → active.
 * This mirrors how the tree badges and row labels classify rows and is the
 * single source of derivation for UI consumption.
 */
export function deriveAlarmStatus(a: ActiveAlarm): AlarmStatus {
  if (a.stale_since) return 'stale';
  if (a.acked_at) return 'acknowledged';
  if (a.underlying_cleared_at && !a.acked_at) return 'awaiting_ack';
  return 'active';
}
